import React, { useEffect, useMemo, useState } from 'react'
import { Box, Dialog, IconButton } from '@mui/material'
import { useTheme } from '@mui/material/styles'
import AddIcon from '@mui/icons-material/Add'
import CloseIcon from '@mui/icons-material/Close'
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import RemoveIcon from '@mui/icons-material/Remove'
import config from '../config.json'

const MIN_ZOOM = 1
const MAX_ZOOM = 3
const ZOOM_STEP = 0.25

const normalizeImageUrl = (imageUrl) => {
  if (!imageUrl || typeof imageUrl !== 'string') return null

  const trimmedUrl = imageUrl.trim()
  if (!trimmedUrl) return null

  if (/^https?:\/\//i.test(trimmedUrl)) return trimmedUrl

  try {
    return new URL(trimmedUrl, config.HOST_NAME).toString()
  } catch (error) {
    return null
  }
}

const controlButtonStyles = {
  width: { xs: 48, sm: 52 },
  height: { xs: 48, sm: 52 },
  backgroundColor: 'rgba(255, 255, 255, 0.14)',
  color: '#FFFFFF',
  border: '1px solid rgba(255, 255, 255, 0.2)',
  backdropFilter: 'blur(18px)',
  boxShadow: '0 14px 32px rgba(0, 0, 0, 0.28)',
  '&:hover': {
    backgroundColor: 'rgba(255, 255, 255, 0.22)',
  },
  '&.Mui-disabled': {
    color: 'rgba(255, 255, 255, 0.45)',
    borderColor: 'rgba(255, 255, 255, 0.16)',
  },
}

const ImageViewer = ({ imageUrls = [], open = false, onClose }) => {
  const theme = useTheme()
  const [activeIndex, setActiveIndex] = useState(0)
  const [zoom, setZoom] = useState(MIN_ZOOM)

  const images = useMemo(
    () => imageUrls.map(normalizeImageUrl).filter(Boolean),
    [imageUrls]
  )

  useEffect(() => {
    if (!open) return

    setActiveIndex(0)
    setZoom(MIN_ZOOM)
  }, [open, images.length])

  useEffect(() => {
    if (!images.length) setActiveIndex(0)
    else if (activeIndex > images.length - 1) setActiveIndex(images.length - 1)
  }, [activeIndex, images.length])

  const showPreviousImage = () => {
    if (images.length <= 1) return

    setActiveIndex((prevIndex) => (
      prevIndex === 0 ? images.length - 1 : prevIndex - 1
    ))
    setZoom(MIN_ZOOM)
  }

  const showNextImage = () => {
    if (images.length <= 1) return

    setActiveIndex((prevIndex) => (
      prevIndex === images.length - 1 ? 0 : prevIndex + 1
    ))
    setZoom(MIN_ZOOM)
  }

  const zoomOut = () => setZoom((prevZoom) => Math.max(MIN_ZOOM, +(prevZoom - ZOOM_STEP).toFixed(2)))
  const zoomIn = () => setZoom((prevZoom) => Math.min(MAX_ZOOM, +(prevZoom + ZOOM_STEP).toFixed(2)))
  const currentImage = images[activeIndex]

  return (
    <Dialog
      fullScreen
      open={open && images.length > 0}
      onClose={onClose}
      sx={{
        zIndex: 999999,
      }}
      PaperProps={{
        sx: {
          background: 'radial-gradient(circle at top, #233246 0%, #0A0F16 58%, #030507 100%)',
          zIndex: 999999,
        },
      }}
    >
      <Box
        sx={{
          position: 'relative',
          width: '100%',
          height: '100%',
          overflow: 'hidden',
          '&::before': {
            content: '""',
            position: 'absolute',
            inset: 0,
            background: 'linear-gradient(180deg, rgba(0, 0, 0, 0.55) 0%, rgba(0, 0, 0, 0.08) 30%, rgba(0, 0, 0, 0.08) 70%, rgba(0, 0, 0, 0.62) 100%)',
            pointerEvents: 'none',
            zIndex: 0,
          },
        }}
      >
        <Box
          sx={{
            position: 'absolute',
            top: { xs: 16, sm: 20 },
            left: { xs: 16, sm: 20 },
            right: { xs: 16, sm: 20 },
            zIndex: 2,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 2,
          }}
        >
          <Box
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              minHeight: { xs: 44, sm: 48 },
              padding: { xs: '10px 14px', sm: '12px 16px' },
              borderRadius: '999px',
              backgroundColor: 'rgba(8, 13, 20, 0.62)',
              border: '1px solid rgba(255, 255, 255, 0.12)',
              backdropFilter: 'blur(18px)',
              color: '#FFFFFF',
              fontSize: { xs: '14px', sm: '16px' },
              fontWeight: theme.typography.fontWeightSemibold,
              letterSpacing: '0.02em',
            }}
          >
            {activeIndex + 1} / {images.length}
          </Box>

          <IconButton
            onClick={onClose}
            aria-label="Close image viewer"
            sx={controlButtonStyles}
          >
            <CloseIcon />
          </IconButton>
        </Box>

        <IconButton
          onClick={showPreviousImage}
          disabled={images.length <= 1}
          aria-label="Previous image"
          sx={{
            ...controlButtonStyles,
            position: 'absolute',
            left: { xs: 12, sm: 20 },
            top: '50%',
            transform: 'translateY(-50%)',
            zIndex: 2,
            display: { xs: 'none', sm: 'inline-flex' },
          }}
        >
          <ChevronLeftIcon />
        </IconButton>

        <IconButton
          onClick={showNextImage}
          disabled={images.length <= 1}
          aria-label="Next image"
          sx={{
            ...controlButtonStyles,
            position: 'absolute',
            right: { xs: 12, sm: 20 },
            top: '50%',
            transform: 'translateY(-50%)',
            zIndex: 2,
            display: { xs: 'none', sm: 'inline-flex' },
          }}
        >
          <ChevronRightIcon />
        </IconButton>

        <Box
          sx={{
            position: 'relative',
            zIndex: 1,
            width: '100%',
            height: '100%',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            padding: {
              xs: '76px 0 108px',
              sm: '88px 24px 108px',
              md: '88px 76px 108px',
            },
          }}
        >
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              width: '100%',
              height: '100%',
              maxWidth: { xs: '100%', md: '1200px' },
              borderRadius: { xs: '0', sm: '24px' },
              backgroundColor: { xs: 'transparent', sm: 'rgba(255, 255, 255, 0.04)' },
              border: { xs: 'none', sm: '1px solid rgba(255, 255, 255, 0.06)' },
              overflow: 'hidden',
            }}
          >
            <Box
              component="img"
              src={currentImage}
              alt={`Vehicle ${activeIndex + 1}`}
              sx={{
                width: { xs: '100%', sm: 'auto' },
                height: { xs: '100%', sm: 'auto' },
                maxWidth: '100%',
                maxHeight: '100%',
                objectFit: 'contain',
                transform: `scale(${zoom})`,
                transformOrigin: 'center center',
                transition: 'transform 0.22s ease',
                userSelect: 'none',
                WebkitUserDrag: 'none',
              }}
            />
          </Box>
        </Box>

        <Box
          sx={{
            position: 'absolute',
            bottom: { xs: 16, sm: 20 },
            left: '50%',
            transform: 'translateX(-50%)',
            display: 'flex',
            alignItems: 'center',
            gap: { xs: 1, sm: 1.5 },
            zIndex: 2,
            width: 'calc(100% - 32px)',
            maxWidth: 'max-content',
            padding: { xs: '10px 12px', sm: '12px 14px' },
            borderRadius: '999px',
            backgroundColor: 'rgba(8, 13, 20, 0.72)',
            border: '1px solid rgba(255, 255, 255, 0.12)',
            backdropFilter: 'blur(20px)',
            boxShadow: '0 18px 44px rgba(0, 0, 0, 0.32)',
          }}
        >
          <IconButton
            onClick={showPreviousImage}
            disabled={images.length <= 1}
            aria-label="Previous image"
            sx={{
              ...controlButtonStyles,
              display: { xs: 'inline-flex', sm: 'none' },
            }}
          >
            <ChevronLeftIcon />
          </IconButton>

          <IconButton
            onClick={showNextImage}
            disabled={images.length <= 1}
            aria-label="Next image"
            sx={{
              ...controlButtonStyles,
              display: { xs: 'inline-flex', sm: 'none' },
            }}
          >
            <ChevronRightIcon />
          </IconButton>

          <IconButton
            onClick={zoomOut}
            disabled={zoom <= MIN_ZOOM}
            aria-label="Zoom out"
            sx={controlButtonStyles}
          >
            <RemoveIcon />
          </IconButton>

          <Box
            sx={{
              minWidth: { xs: 54, sm: 60 },
              textAlign: 'center',
              color: '#FFFFFF',
              fontSize: { xs: '13px', sm: '14px' },
              fontWeight: theme.typography.fontWeightSemibold,
              letterSpacing: '0.01em',
            }}
          >
            {Math.round(zoom * 100)}%
          </Box>

          <IconButton
            onClick={zoomIn}
            disabled={zoom >= MAX_ZOOM}
            aria-label="Zoom in"
            sx={controlButtonStyles}
          >
            <AddIcon />
          </IconButton>
        </Box>
      </Box>
    </Dialog>
  )
}

export default ImageViewer
